import { useState } from "react";
import axios from "axios";
import AppShell from "../components/layout/AppShell";
import AppFooter from "../components/layout/AppFooter";
import TopNav from "../components/layout/TopNav";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

const btnLink = { background: "none", border: "none", color: "#a78bfa", cursor: "pointer", textDecoration: "none", padding: 0, fontWeight: 600, fontSize: 13 };
const btnSubtle = { background: "none", border: "none", color: "rgba(220,220,235,0.75)", cursor: "pointer", padding: 0, fontSize: 13 };

export default function Login({ onLogin, onNavigate }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [step, setStep] = useState("email"); // "email" | "login" | "register"
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [humanCheck, setHumanCheck] = useState(false);

  const checkEmail = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/check-email`, { email });
      setStep(res.data.exists ? "login" : "register");
    } catch {
      setError("Erreur lors de la vérification.");
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (step === "register") {
        const blockedDomains = ['yopmail.com', 'tempmail.com', 'guerrillamail.com', 'mailinator.com', 'throwaway.email', 'temp-mail.org', 'fakeinbox.com'];
        const domain = email.split('@')[1]?.toLowerCase();
        if (blockedDomains.includes(domain)) {
          setError('Les adresses email jetables ne sont pas acceptées.');
          setSubmitting(false);
          return;
        }
        await axios.post(`${API}/auth/register`, { email, password });
        const loginRes = await axios.post(`${API}/auth/login`, { email, password });
        const token = loginRes.data.token;
        if (firstName || lastName || phone) {
          await axios.put(`${API}/users/me`,
            { first_name: firstName, last_name: lastName, phone },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        localStorage.setItem("token", token);
        onLogin?.(token);
      } else {
        const res = await axios.post(`${API}/auth/login`, { email, password });
        localStorage.setItem("token", res.data.token);
        onLogin?.(res.data.token);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Erreur de connexion.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetToEmail = () => { setStep("email"); setError(""); setPassword(""); };

  return (
    <AppShell
      header={<TopNav isLoggedIn={false} hideLogin={true} onNavigate={onNavigate} />}
      footer={<AppFooter onNavigate={onNavigate} />}
      onNavigate={onNavigate}
    >
      <div className="auth">
        <div className="auth-card section-card">
          <div className="auth-head">
            <h2 className="page-title" style={{ margin: 0 }}>
              {step === "email" ? "Connexion" : step === "login" ? "Bon retour 👋" : "Créer un compte"}
            </h2>
            <p className="auth-sub">
              {step === "email" && "Accède à ton tableau de bord Solvya."}
              {step === "login" && "Entre ton mot de passe pour continuer."}
              {step === "register" && "Aucun compte trouvé, on en crée un."}
            </p>
          </div>

          {error && <div className="alert alert-error" role="alert">{error}</div>}

          {/* ── ÉTAPE EMAIL ── */}
          {step === "email" && (
            <form onSubmit={checkEmail} className="auth-form">
              <div className="auth-email-block">
                <label className="field">
                  <span className="field-label">E-mail</span>
                  <input
                    className="input"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ex: contact@solvya.io"
                    required
                  />
                </label>
                <button type="submit" className="auth-primary" disabled={submitting}
                  style={{ borderRadius: 12, fontSize: 14, fontWeight: 600, padding: '12px 16px', cursor: 'pointer', background: 'linear-gradient(180deg, rgba(110,86,207,0.4), rgba(110,86,207,0.25))', border: '1px solid rgba(110,86,207,0.6)', color: '#fff', transition: 'all 0.2s ease', width: '100%', }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(110,86,207,0.55), rgba(110,86,207,0.38))'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(110,86,207,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(110,86,207,0.4), rgba(110,86,207,0.25))'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {submitting ? "Vérification..." : "Continuer avec l'e-mail"}
                </button>
              </div>

              <div className="auth-sep"><span>ou</span></div>

              <button type="button" className="auth-oauth" onClick={() => (window.location.href = `${API}/auth/google`)}
                style={{
                  background: 'rgba(110,86,207,0.08)',
                  border: '1px solid rgba(110,86,207,0.3)',
                  borderRadius: 12,
                  padding: '12px 16px',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  width: '100%',
                  transition: 'all 0.2s ease',
                }}
              >
                <span className="auth-oauth-icon"><img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" /></span>
                Continuer avec Google
              </button>

              <div style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "rgba(220,220,235,0.85)" }}>
                Pas encore de compte ?{" "}
                <button type="button" style={{ ...btnLink, fontSize: 13 }}
                  onClick={() => { if (!email) { setError("Entre d'abord ton e-mail."); return; } setStep("register"); setError(""); }}
                >
                  Créer un compte →
                </button>
              </div>
            </form>
          )}

          {/* ── ÉTAPE LOGIN ── */}
          {step === "login" && (
            <form onSubmit={submit} className="auth-form">
              <div className="auth-email-block">
                <label className="field">
                  <span className="field-label">E-mail</span>
                  <input className="input" type="email" value={email} disabled />
                </label>
                <label className="field">
                  <span className="field-label">Mot de passe</span>
                  <input
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </label>
                <button type="submit" className="auth-primary" disabled={submitting}
                  style={{borderRadius: 12, fontSize: 14, fontWeight: 600, padding: '12px 16px',cursor: 'pointer',background: 'linear-gradient(180deg, rgba(110,86,207,0.4), rgba(110,86,207,0.25))',border: '1px solid rgba(110,86,207,0.6)',color: '#fff',transition: 'all 0.2s ease',width: '100%',}}
                  onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(110,86,207,0.55), rgba(110,86,207,0.38))'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(110,86,207,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(110,86,207,0.4), rgba(110,86,207,0.25))'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {submitting ? "Chargement..." : "Se connecter"}
                </button>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <button type="button" style={{ ...btnSubtle, fontSize: 12 }} onClick={resetToEmail}>
                    ← Changer d'e-mail
                  </button>
                  <button type="button" style={{ ...btnSubtle, fontSize: 12, textDecoration: "underline" }}
                    onClick={() => alert("Fonctionnalité à venir — contactez le support.")}
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* ── ÉTAPE REGISTER ── */}
          {step === "register" && (
            <form onSubmit={submit} className="auth-form">
              <div className="auth-email-block">
                <label className="field">
                  <span className="field-label">E-mail</span>
                  <input className="input" type="email" value={email} disabled />
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <label className="field">
                    <span className="field-label">Prénom</span>
                    <input className="input" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Prénom" />
                  </label>
                  <label className="field">
                    <span className="field-label">Nom</span>
                    <input className="input" type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Nom" />
                  </label>
                </div>
                <label className="field">
                  <span className="field-label">Téléphone <span className="muted" style={{ fontSize: 11 }}>(optionnel)</span></span>
                  <input className="input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+33 6 00 00 00 00" />
                </label>
                <label className="field">
                  <span className="field-label">Mot de passe</span>
                  <input
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </label>
                <div
                  onClick={() => setHumanCheck(v => !v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    borderRadius: 12, cursor: 'pointer',
                    background: humanCheck ? 'rgba(74,222,128,0.08)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${humanCheck ? 'rgba(74,222,128,0.3)' : 'rgba(255,255,255,0.08)'}`,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                    border: `2px solid ${humanCheck ? '#4ade80' : 'rgba(255,255,255,0.2)'}`,
                    background: humanCheck ? '#4ade80' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease', color: '#fff', fontSize: 14, fontWeight: 800,
                  }}>
                    {humanCheck && '✓'}
                  </div>
                  <span style={{ fontSize: 13, color: 'rgba(220,220,235,0.85)' }}>Je ne suis pas un robot</span>
                </div>
                <button type="submit" className="auth-primary" disabled={submitting || !humanCheck}
                  style={{ borderRadius: 12, fontSize: 14, fontWeight: 600, padding: '12px 16px', cursor: humanCheck ? 'pointer' : 'not-allowed', background: 'linear-gradient(180deg, rgba(110,86,207,0.4), rgba(110,86,207,0.25))', border: '1px solid rgba(110,86,207,0.6)', color: '#fff', transition: 'all 0.2s ease', width: '100%', opacity: humanCheck ? 1 : 0.5, }}
                  onMouseEnter={e => { if (humanCheck) { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(110,86,207,0.55), rgba(110,86,207,0.38))'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(110,86,207,0.3)'; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(110,86,207,0.4), rgba(110,86,207,0.25))'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {submitting ? "Chargement..." : "Créer mon compte"}
                </button>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <button type="button" style={{ ...btnSubtle, fontSize: 13 }} onClick={resetToEmail}>
                    ← Changer d'e-mail
                  </button>
                  <div style={{ fontSize: 13, color: "rgba(220,220,235,0.75)" }}>
                    Déjà un compte ?{" "}
                    <button type="button" style={{ ...btnLink, fontSize: 13 }}
                      onClick={() => { setStep("login"); setError(""); }}
                    >
                      Se connecter →
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </AppShell>
  );
}
